import {
  createBankTitleMap,
  renderDataGridLocaleText,
  formatProjectCompanyCodes,
  summarizeBookProjectBanks,
  flattenProductBankAssignments,
} from 'utils';
import {
  DataGridBox,
  ShowProducts,
  CustomToolbar,
  ZebraDataGrid,
  ShowCompanies,
  BankUsageChip,
  ShowProductBanks,
  getBankStatusLabel,
  CustomNoRowsOverlay,
  getProductCompanyStatusLabel,
} from 'components';
import {
  GridColDef,
  useGridApiRef,
  GridRenderCellParams,
  GridRowSelectionModel,
  getGridStringOperators,
} from '@mui/x-data-grid-premium';
import { EXPORT_TYPE_ENUM } from 'enums';
import type { IntlShape } from 'react-intl';
import apiService from 'library/api-service';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import BusinessIcon from '@mui/icons-material/Business';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import projectsActions from 'store/features/projects/actions';
import gibTypesActions from 'store/features/gib-types/actions';
import usedBanksActions from 'store/features/used-banks/actions';
import { Book, BookCompany, BookProject, Project } from 'library/types';
import { Alert, Box, IconButton, Tooltip, Typography } from '@mui/material';
import connectionTypesActions from 'store/features/connection-types/actions';
import { selectProjects, selectProjectsIsEmpty } from 'store/features/projects/selector';
import { selectGibTypes, selectGibTypesIsEmpty } from 'store/features/gib-types/selector';
import { selectUsedBanks, selectUsedBanksIsEmpty } from 'store/features/used-banks/selector';
import { selectConnectionTypes, selectConnectionTypesIsEmpty } from 'store/features/connection-types/selector';

const formatBookBankAssignments = (
  projects: BookProject[],
  products: Project[],
  bankTitleMap: Map<number, string>,
  intl: IntlShape
): string =>
  (Array.isArray(projects) ? projects : [])
    .flatMap((project) => {
      const productTitle =
        products.find((product) => product.id === project.id)?.title ??
        `${intl.formatMessage({ id: 'project.table.product', defaultMessage: 'Ürün' })} #${project.id}`;

      return flattenProductBankAssignments(project.banks).map(({ bankId, companyCode, status }) => {
        const bankTitle =
          bankTitleMap.get(bankId) ??
          `${intl.formatMessage({ id: 'project.table.bank', defaultMessage: 'Banka' })} #${bankId}`;

        return `${productTitle} | ${companyCode} | ${bankTitle} | ${getBankStatusLabel(status, intl)}`;
      });
    })
    .join('\n');

const formatBookProducts = (projects: BookProject[], products: Project[], intl: IntlShape): string =>
  (Array.isArray(projects) ? projects : [])
    .map((project) => {
      const productTitle =
        products.find((product) => product.id === project.id)?.title ??
        `${intl.formatMessage({ id: 'project.table.product', defaultMessage: 'Ürün' })} #${project.id}`;
      const companyCodes = formatProjectCompanyCodes(project.companyCodes, (status) =>
        getProductCompanyStatusLabel(status, intl)
      );

      return companyCodes ? `${productTitle}: ${companyCodes}` : productTitle;
    })
    .join('\n');

type ProductsDialogContext = {
  projects: BookProject[];
  companies: BookCompany[];
  bookTitle: string;
};

const Report = () => {
  // other hooks
  const intl = useIntl();
  const apiRef = useGridApiRef();

  // redux hooks
  const dispatch = useAppDispatch();

  const products = useAppSelector(selectProjects);
  const isEmptyProducts = useAppSelector(selectProjectsIsEmpty);

  const connectionTypes = useAppSelector(selectConnectionTypes);
  const isEmptyConnectionTypes = useAppSelector(selectConnectionTypesIsEmpty);

  const gibTypes = useAppSelector(selectGibTypes);
  const isEmptyGibTypes = useAppSelector(selectGibTypesIsEmpty);

  const usedBanks = useAppSelector(selectUsedBanks);
  const isEmptyUsedBanks = useAppSelector(selectUsedBanksIsEmpty);

  // useState hooks
  const [data, setData] = useState<Book[]>([]);
  const [loading, setLoading] = useState(false);

  const [openShowCompanies, setOpenShowCompanies] = useState(false);
  const [clickedCompanies, setClickedCompanies] = useState<BookCompany[]>([]);

  const [productsDialogContext, setProductsDialogContext] = useState<ProductsDialogContext | null>(null);

  const [openShowProductBanks, setOpenShowProductBanks] = useState(false);
  const [clickedProjectsForBanks, setClickedProjectsForBanks] = useState<BookProject[]>([]);
  const [clickedBookTitle, setClickedBookTitle] = useState<string>('');

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const incompleteAdaptation = isEmptyProducts || isEmptyConnectionTypes || isEmptyGibTypes;
  const bankTitleMap = useMemo(() => createBankTitleMap(usedBanks), [usedBanks]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'companyName',
        headerName: intl.formatMessage({ id: 'report.companyName', defaultMessage: 'Proje Adı' }),
        flex: 0.5,
      },
      {
        field: 'companies',
        headerName: intl.formatMessage({ id: 'report.companies', defaultMessage: 'Şirketler' }),
        flex: 0.4,
        headerAlign: 'center',
        valueGetter: (value: BookCompany[]) => {
          return (Array.isArray(value) ? value : []).map((company) => company.title).join(', ');
        },
        renderCell: (params: GridRenderCellParams) => {
          const companies: BookCompany[] = Array.isArray(params.row.companies) ? params.row.companies : [];
          const actionLabel = intl.formatMessage({ id: 'report.showCompanies', defaultMessage: 'Şirketleri Göster' });

          return companies.length ? (
            <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" height="100%">
              <Tooltip title={<FormattedMessage id="report.showCompanies" defaultMessage="Şirketleri Göster" />}>
                <IconButton
                  sx={{ border: 'none' }}
                  aria-label={actionLabel}
                  onClick={(event) => {
                    event.stopPropagation();
                    setClickedCompanies(companies);
                    handleClickOpenShowCompanies();
                  }}>
                  <BusinessIcon />
                </IconButton>
              </Tooltip>
              <Typography marginLeft={companies.length < 10 ? '8px' : '0px'}>{`${companies.length} ${
                intl.locale === 'tr' ? 'Şirket' : 'Company'
              }`}</Typography>
            </Box>
          ) : null;
        },
        filterOperators: getGridStringOperators().filter(
          (operator) => operator.value === 'contains' || operator.value === 'doesNotContain'
        ),
      },
      {
        field: 'projects',
        headerName: intl.formatMessage({ id: 'report.projects', defaultMessage: 'Ürünler' }),
        flex: 0.4,
        headerAlign: 'center',
        valueGetter: (_, row) => formatBookProducts(row.projects, products, intl),
        valueFormatter: (value: string) => value,
        renderCell: (params: GridRenderCellParams) => {
          const bookProjects: BookProject[] = Array.isArray(params.row.projects) ? params.row.projects : [];
          const value = bookProjects.map((product) => product.id);
          const actionLabel = intl.formatMessage({ id: 'report.showProducts', defaultMessage: 'Ürünleri Göster' });

          return value.length ? (
            <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" height="100%">
              <Tooltip title={<FormattedMessage id="report.showProducts" defaultMessage="Ürünleri Göster" />}>
                <IconButton
                  sx={{ border: 'none' }}
                  aria-label={actionLabel}
                  onClick={(event) => {
                    event.stopPropagation();
                    setProductsDialogContext({
                      projects: bookProjects,
                      companies: Array.isArray(params.row.companies) ? params.row.companies : [],
                      bookTitle: params.row.companyName || '',
                    });
                  }}>
                  <AccountTreeIcon />
                </IconButton>
              </Tooltip>
              <Typography marginLeft={value.length < 10 ? '8px' : '0px'}>{`${value.length} ${
                intl.locale === 'tr' ? 'Ürün' : 'Product'
              }`}</Typography>
            </Box>
          ) : null;
        },
      },
      {
        field: 'bankAssignments',
        headerName: intl.formatMessage({ id: 'report.bankAssignments', defaultMessage: 'Banka Atamaları' }),
        flex: 0.4,
        minWidth: 170,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (_, row) => formatBookBankAssignments(row.projects, products, bankTitleMap, intl),
        valueFormatter: (value: string) => value,
        renderCell: (params: GridRenderCellParams) => {
          const projects: BookProject[] = Array.isArray(params.row.projects) ? params.row.projects : [];
          const summary = summarizeBookProjectBanks(projects);
          const actionLabel = intl.formatMessage({
            id: 'report.showBankDistribution',
            defaultMessage: 'Banka dağılımını göster',
          });

          return (
            <Box display="flex" alignItems="center" justifyContent="center" height="100%" width="100%">
              <BankUsageChip
                assignmentCount={summary.assignmentCount}
                actionLabel={actionLabel}
                onClick={() => {
                  setClickedProjectsForBanks(projects);
                  setClickedBookTitle(params.row.companyName || '');
                  setOpenShowProductBanks(true);
                }}
              />
            </Box>
          );
        },
        sortable: false,
      },
      {
        field: 'connectTypeId',
        headerName: intl.formatMessage({ id: 'report.connectTypeId', defaultMessage: 'Bağlantı Türü' }),
        flex: 0.25,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value: number | null) => {
          const connectType = connectionTypes.find((connectionType) => value === connectionType.id);

          return connectType?.title ?? '';
        },
        type: 'singleSelect',
        valueOptions: () => connectionTypes.map((connectionType) => connectionType.title),
      },
      {
        field: 'gibTypeId',
        headerName: intl.formatMessage({ id: 'report.gibTypeId', defaultMessage: 'GİB Yöntemi' }),
        flex: 0.4,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value: number | null) => {
          const gibType = gibTypes.find((gibType) => value === gibType.id);

          return gibType?.title ?? '';
        },
        type: 'singleSelect',
        valueOptions: () => gibTypes.map((gibType) => gibType.title),
      },
    ],
    [intl, products, connectionTypes, gibTypes, bankTitleMap]
  );

  // async helper functions
  const getValues = async () => {
    try {
      setLoading(true);

      const response = await apiService.getBooks();

      if (response.isSuccess) {
        setData(response.data);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const onClickDownloadHandler = async (exportType: EXPORT_TYPE_ENUM) => {
    const fileName = intl.formatMessage({ id: 'report.exportFileName', defaultMessage: 'proje-raporu' });

    if (exportType === EXPORT_TYPE_ENUM.EXCEL) {
      await apiRef.current.exportDataAsExcel({ fileName });
      return;
    }

    apiRef.current.exportDataAsCsv({ fileName, utf8WithBom: true });
  };

  // sync helper functions
  const handleCloseShowCompanies = () => setOpenShowCompanies(false);
  const handleClickOpenShowCompanies = () => setOpenShowCompanies(true);

  const handleCloseShowProductBanks = () => setOpenShowProductBanks(false);

  // useEffect hooks
  useEffect(() => {
    if (!products.length && !isEmptyProducts) {
      dispatch(projectsActions.getProjects());
    }
  }, [products, isEmptyProducts, dispatch]);

  useEffect(() => {
    if (!connectionTypes.length && !isEmptyConnectionTypes) {
      dispatch(connectionTypesActions.getConnectionTypes());
    }
  }, [connectionTypes, isEmptyConnectionTypes, dispatch]);

  useEffect(() => {
    if (!gibTypes.length && !isEmptyGibTypes) {
      dispatch(gibTypesActions.getGibTypes());
    }
  }, [gibTypes, isEmptyGibTypes, dispatch]);

  useEffect(() => {
    if (!usedBanks.length && !isEmptyUsedBanks) {
      dispatch(usedBanksActions.getUsedBanks());
    }
  }, [usedBanks, isEmptyUsedBanks, dispatch]);

  useEffect(() => {
    getValues();
  }, []);

  return incompleteAdaptation ? (
    <Alert severity="info" icon={false}>
      <Typography>
        <FormattedMessage
          id="report.completeAdaptationsText"
          defaultMessage="Sayfayı görüntülemek için lütfen ürün, bağlantı türü ve GİB yöntemi uyarlamalarını tamamlayın."
        />
      </Typography>
    </Alert>
  ) : (
    <>
      <ShowCompanies
        open={openShowCompanies}
        handleClose={handleCloseShowCompanies}
        clickedCompanies={clickedCompanies}
      />
      <ShowProducts
        open={Boolean(productsDialogContext)}
        handleClose={() => setProductsDialogContext(null)}
        clickedProducts={productsDialogContext?.projects ?? []}
        allProducts={products}
        companies={productsDialogContext?.companies ?? []}
        bookTitle={productsDialogContext?.bookTitle}
      />
      <ShowProductBanks
        open={openShowProductBanks}
        handleClose={handleCloseShowProductBanks}
        projects={clickedProjectsForBanks}
        allProducts={products}
        usedBanks={usedBanks}
        bookTitle={clickedBookTitle}
      />
      <DataGridBox>
        <ZebraDataGrid<Book>
          apiRef={apiRef}
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText(intl)}
          rows={data}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () =>
              CustomToolbar({
                multiExport: true,
                exportAsExcelHandler: () => onClickDownloadHandler(EXPORT_TYPE_ENUM.EXCEL),
                exportAsCsvHandler: () => onClickDownloadHandler(EXPORT_TYPE_ENUM.CSV),
              }),
          }}
          slotProps={{
            filterPanel: {
              filterFormProps: {
                logicOperatorInputProps: {
                  variant: 'outlined',
                  size: 'small',
                },
                columnInputProps: {
                  variant: 'outlined',
                  size: 'small',
                  sx: { mt: 'auto' },
                },
                operatorInputProps: {
                  variant: 'outlined',
                  size: 'small',
                  sx: { mt: 'auto' },
                },
                valueInputProps: {
                  InputComponentProps: {
                    variant: 'outlined',
                    size: 'small',
                  },
                },
              },
            },
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default Report;
